import { AudioMixer, AppEdition } from './AudioMixer';
import { PresetSoundSynthesizer, PresetSoundType } from './PresetSoundSynthesizer';
import { CustomVoiceManager, VoiceActionType } from './CustomVoiceManager';

export interface PhysicsDomainEvent {
  type: string;
  intensity: number;
}

export interface PhysicsEventHandlerOptions {
  mixer: AudioMixer;
  presetSynthesizer: PresetSoundSynthesizer;
  customVoiceManager: CustomVoiceManager;
}

interface EventMapping {
  actionType: VoiceActionType;
  presetSound: PresetSoundType;
  cooldownMs: number;
}

const EVENT_MAPPINGS: Record<string, EventMapping> = {
  impact: {
    actionType: 'tap',
    presetSound: 'plump',
    cooldownMs: 60,
  },
  tap: {
    actionType: 'tap',
    presetSound: 'plump',
    cooldownMs: 60,
  },
  release: {
    actionType: 'drag_release',
    presetSound: 'bounce',
    cooldownMs: 150,
  },
  drag_release: {
    actionType: 'drag_release',
    presetSound: 'bounce',
    cooldownMs: 150,
  },
  wobble: {
    actionType: 'wobble',
    presetSound: 'water',
    cooldownMs: 2500,
  },
};

/**
 * 物理イベント連携＆トリガーハンドラコンポーネント (PhysicsEventHandlerComponent)
 * 物理変形エンジンからのドメインイベントをACL（腐敗防止層）として翻訳し、
 * クールダウン制御（スロットリング）、SE・ボイスの同時発音および自動ダッキングを実行する。
 */
export class PhysicsEventHandler {
  private mixer: AudioMixer;
  private presetSynthesizer: PresetSoundSynthesizer;
  private customVoiceManager: CustomVoiceManager;

  // 各アクション種別ごとの最終発火時刻
  private lastTriggerTimes: Map<VoiceActionType, number> = new Map();

  // ボイス再生中フラグ（複数同時再生時のダッキング多重復帰防止）
  private activeDuckingCount: number = 0;

  constructor(options: PhysicsEventHandlerOptions) {
    this.mixer = options.mixer;
    this.presetSynthesizer = options.presetSynthesizer;
    this.customVoiceManager = options.customVoiceManager;
  }

  /**
   * 物理ドメインイベントの受信・処理
   */
  public handlePhysicsEvent(event: PhysicsDomainEvent): void {
    // 1. エディションガード（Web無料版は即座に破棄）
    if (this.mixer.getEdition() === 'web_free') {
      return;
    }

    if (!event || !event.type) return;

    // 2. ACLイベント翻訳
    const mapping = EVENT_MAPPINGS[event.type];
    if (!mapping) {
      return;
    }

    const { actionType, presetSound, cooldownMs } = mapping;
    const now = Date.now();
    const lastTime = this.lastTriggerTimes.get(actionType) ?? 0;

    // 3. クールダウン（スロットリング）チェック
    if (now - lastTime < cooldownMs) {
      return;
    }
    this.lastTriggerTimes.set(actionType, now);

    const intensity = typeof event.intensity === 'number' ? event.intensity : 0.5;

    // 4. カスタムボイスの有無チェック
    const hasVoice = this.customVoiceManager.hasVoice(actionType);

    if (hasVoice) {
      // ボイスが存在する場合: SE音量を一時減衰 (40% / -8dB) させて同時再生
      this.activeDuckingCount++;
      this.mixer.setSEDucking(0.4);

      // SE再生（減衰音量で発音）
      this.presetSynthesizer.playPresetSE(presetSound, 0.4, intensity);

      // ボイス再生
      const played = this.customVoiceManager.playVoice(actionType, () => {
        // 再生終了時: ダッキング解除 (100msで復帰)
        this.activeDuckingCount = Math.max(0, this.activeDuckingCount - 1);
        if (this.activeDuckingCount === 0) {
          this.mixer.resetSEDucking();
        }
      });

      if (!played) {
        // ボイス再生に失敗した場合はダッキングを即座にリセット
        this.activeDuckingCount = Math.max(0, this.activeDuckingCount - 1);
        if (this.activeDuckingCount === 0) {
          this.mixer.resetSEDucking();
        }
      }
    } else {
      // ボイス未登録時: SEを標準音量で発音
      this.presetSynthesizer.playPresetSE(presetSound, 1.0, intensity);
    }
  }

  /**
   * エディション状態の更新
   */
  public setEdition(edition: AppEdition): void {
    this.mixer.setEdition(edition);
  }

  public getCooldownMs(actionType: VoiceActionType): number {
    const entry = Object.values(EVENT_MAPPINGS).find((m) => m.actionType === actionType);
    return entry ? entry.cooldownMs : 0;
  }
}
